import React from 'react';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import SummaryCards from './SummaryCards';
import DataTable from './DataTable';
import { ProcessedMember, Member } from '../types';

interface ResultsScreenProps {
  members: ProcessedMember[];
  originalMembers: Member[];
  onBack: () => void;
  onReprocess: () => void;
  onExport?: () => void;
}

const ResultsScreen: React.FC<ResultsScreenProps> = ({
  members,
  originalMembers,
  onBack,
  onReprocess,
  onExport
}) => {
  return (
    <div className="min-h-screen bg-secondary-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <button
              onClick={onBack}
              className="btn-secondary flex items-center space-x-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Upload New File</span>
            </button>
            
            <div>
              <h1 className="text-2xl font-semibold text-secondary-900">
                Hot Seat Analysis Results
              </h1>
              <p className="text-secondary-600">
                {members.length} qualified members out of {originalMembers.length} total
              </p>
            </div>
          </div>
          
          <button
            onClick={onReprocess}
            className="btn-secondary flex items-center space-x-2"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Reprocess</span>
          </button>
        </div>

        {/* Summary Cards */}
        <SummaryCards members={members} totalMembers={originalMembers.length} />

        {/* Results Table */}
        <DataTable members={members} onExport={onExport} />

        {/* Insights Section */}
        {members.length > 0 && (
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Performers */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                Top 10 Hot Seat Candidates
              </h3>
              <div className="space-y-3">
                {members.slice(0, 10).map((member, index) => (
                  <div key={member.sNo} className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-semibold text-primary-700">
                          {index + 1}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-secondary-900">
                          {member.memberName}
                        </p>
                        <p className="text-sm text-secondary-500">
                          {member.yearsInBNI} years in BNI
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-primary-600">
                        {member.finalScore.toFixed(1)}
                      </p>
                      <p className="text-xs text-secondary-500">
                        Final Score
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Key Insights */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                Key Insights
              </h3>
              <div className="space-y-4">
                <div className="p-3 bg-primary-50 rounded-lg">
                  <p className="text-sm font-medium text-primary-900 mb-1">
                    Average Contribution Gap
                  </p>
                  <p className="text-2xl font-bold text-primary-600">
                    ₹{(members.reduce((sum, m) => sum + m.contributionGap, 0) / members.length).toFixed(0)}
                  </p>
                </div>
                
                <div className="p-3 bg-success-50 rounded-lg">
                  <p className="text-sm font-medium text-success-900 mb-1">
                    Average Experience
                  </p>
                  <p className="text-2xl font-bold text-success-600">
                    {(members.reduce((sum, m) => sum + m.yearsInBNI, 0) / members.length).toFixed(1)} years
                  </p>
                </div>
                
                <div className="p-3 bg-accent-50 rounded-lg">
                  <p className="text-sm font-medium text-accent-900 mb-1">
                    Hot Seat Members (Top 10)
                  </p>
                  <p className="text-2xl font-bold text-accent-600">
                    {Math.min(10, members.length)} members
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* No Results Message */}
        {members.length === 0 && (
          <div className="card p-12 text-center">
            <div className="text-secondary-500">
              <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">📊</span>
              </div>
              <h3 className="text-lg font-medium text-secondary-900 mb-2">
                No Qualified Members Found
              </h3>
              <p className="text-secondary-600 mb-4">
                No members meet the qualification criteria (Green Score &gt; 80 &amp; Revenue Received &lt; TYFCB Given).
              </p>
              <p className="text-sm text-secondary-500">
                Consider reviewing your data or adjusting the scoring criteria.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResultsScreen;
